import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type {
  Invitation,
  InvitationType,
  InvitationDisplayOptions,
} from "@core/features/invitation";

type Args = {
  organizationId: number;
};

type MutationArgs = {
  programId: number;
  templateId: number;
  /** Invitation type ('orgAdmin' or 'participant') */
  type: InvitationType;
  /** Optional custom name for the invitation */
  name?: string;
  /** Optional display options for the invitation */
  displayOptions?: InvitationDisplayOptions;
  /** Optional maximum number of times invitation can be used */
  maxUses?: number;
  /** Optional user ID to own the invitation */
  ownerUserId?: number;
};

/**
 * Create or get an existing invitation for an organization.
 * Uses get-or-create pattern - if one of the specified type already exists, returns it.
 */
export function useCreateOrganizationInvitation({ organizationId }: Args) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      programId,
      templateId,
      type,
      name,
      displayOptions,
      maxUses,
      ownerUserId,
    }: MutationArgs) => {
      return (await apiRequest(
        `/organization/${organizationId}/invitations/create/`,
        "POST",
        {
          program_id: programId,
          template_id: templateId,
          type,
          ...(name && { name }),
          ...(displayOptions && { display_options: displayOptions }),
          ...(maxUses !== undefined && { max_uses: maxUses }),
          ...(ownerUserId !== undefined && { owner_user_id: ownerUserId }),
        }
      )) as Invitation;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["organization", organizationId, "invitations"],
      });
    },
  });
}
